import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";

import { Alert, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { applyFilters, parseFilters } from "@/lib/filters";

import { RefreshButton } from "./RefreshButton";
import { getOrdersTab, ORDERS_QUERY_KEY, type OrderRow } from "./api";
import {
  ORDERS_VIEWS, orderStore, orderValue, ordersBanner, ordersErrorMessage, ordersPendingLine, ordersRank, ordersRows,
  ordersStoreLabel, ordersThresholdLine, ordersUnattributedLine, ordersView, ordersWindowLine, parisDateTime,
} from "./format";
import { useRefresh } from "./useRefresh";

const PAGE_SIZE = 50;

/**
 * T6 Orders (REQ-ORD-001, C6 v1.9): the past seven days' orders in three views, each ranked inside one store and
 * currency; every row shows its order id, channel and location, and the value in its own currency.
 */
export function OrdersTab({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const values = parseFilters(search);
  const filterKey = JSON.stringify(values);
  const [pageIndex, setPageIndex] = useState(0);

  const query = useQuery({ queryKey: ORDERS_QUERY_KEY, queryFn: getOrdersTab });
  const artifact = query.data?.data.artifact ?? null;
  const latestRun = query.data?.data.latest_run ?? null;
  // A manual refresh is a full run, which reads the orders; the Xero button belongs to the Stock tab.
  const refresh = useRefresh(areaName, latestRun, false);

  const view = ordersView(values);
  const rows = useMemo(() => ordersRows(artifact?.rows ?? [], values), [artifact, filterKey]);
  const lastPage = Math.max(0, Math.ceil(rows.length / PAGE_SIZE) - 1);
  const page = Math.min(pageIndex, lastPage);
  const pageRows = rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const banner = ordersBanner(artifact, latestRun, { stalledRunId: refresh.stalledRunId });
  const windowLine = ordersWindowLine(artifact);
  const thresholdLine = ordersThresholdLine(artifact);
  const pendingLine = ordersPendingLine(artifact);
  const unattributedLine = ordersUnattributedLine(artifact);
  const tiles = artifact?.tiles ?? null;

  const filters: FilterSpec[] = [
    { name: "view", label: "View", kind: "choice", options: ORDERS_VIEWS },
    { name: "search", label: "Search order ID", kind: "text" },
    {
      name: "store", label: "Store", kind: "choice",
      options: (artifact?.filters.channel_locations ?? []).map((cl) => ({ value: cl, label: ordersStoreLabel(cl) })),
    },
    {
      name: "currency", label: "Currency", kind: "choice",
      options: (artifact?.filters.currencies ?? []).map((c) => ({ value: c, label: c })),
    },
  ];

  const columns: ColumnDef<OrderRow, unknown>[] = [
    // The rank the column holds, by name: in the threshold view it is the store's value rank, not a threshold position.
    { id: "rank", header: view === "units" ? "Units rank" : "Value rank", cell: ({ row }) => ordersRank(row.original, view) ?? "" },
    { id: "order", header: "Order ID", cell: ({ row }) => <span className="font-medium">{row.original.order_id}</span> },
    { id: "store", header: "Store", cell: ({ row }) => orderStore(row.original) },
    {
      id: "value", header: "Value",
      cell: ({ row }) => (
        <span>
          {orderValue(row.original)}
          {row.original.over_threshold ? <Badge variant="destructive" className="ml-2">OVER THRESHOLD</Badge> : null}
        </span>
      ),
    },
    { id: "units", header: "Units", cell: ({ row }) => row.original.units ?? "no items" },
    { id: "placed", header: "Placed", cell: ({ row }) => parisDateTime(row.original.created_at) ?? "" },
  ];

  const empty = !artifact
    ? "No orders have been read yet."
    : view === "threshold"
      ? "No order reached its threshold in the window."
      : "No orders match these filters.";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        {banner.kind !== "ok" ? (
          <Alert variant={banner.kind === "failed" ? "destructive" : "default"} className="max-w-3xl">
            <AlertTitle>{banner.text}</AlertTitle>
          </Alert>
        ) : <div />}
        <RefreshButton
          state={refresh.state}
          message={refresh.message}
          progress={refresh.progress}
          xeroAvailable={false}
          onStart={(source) => void refresh.start(source)}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {[
          ["Orders in the window", tiles?.orders ?? ""],
          ["At or over threshold", tiles?.over_threshold ?? ""],
          ["No price yet", tiles?.value_pending ?? ""],
        ].map(([label, value]) => (
          <Card key={String(label)}>
            <CardHeader className="pb-1"><CardTitle className="text-sm font-normal">{label}</CardTitle></CardHeader>
            <CardContent className="text-2xl font-bold">
              {value}
              {/* The tiles count the whole window; the filters narrow the table only (R2-1). */}
              {tiles ? <span className="block text-xs font-normal text-muted-foreground">all stores</span> : null}
            </CardContent>
          </Card>
        ))}
      </div>

      {windowLine ? <p className="text-sm text-muted-foreground">{windowLine}</p> : null}
      {thresholdLine ? <p className="text-sm text-muted-foreground">{thresholdLine}</p> : null}
      {pendingLine ? <p className="text-sm text-muted-foreground">{pendingLine}</p> : null}
      {unattributedLine ? <p role="status" className="text-sm text-ks-ink">{unattributedLine}</p> : null}

      <div>
        <FilterBar
          filters={filters}
          values={values}
          onChange={(next) => {
            setPageIndex(0);
            const navigateOptions: Record<string, unknown> = {
              search: (prev: Record<string, unknown>) => applyFilters(prev, next),
              replace: true,
            };
            void navigate(navigateOptions as never);
          }}
        />
        {query.isError ? (
          <p role="alert" className="text-sm text-ks-danger">{ordersErrorMessage(query.error, areaName)}</p>
        ) : (
          <DataTable
            columns={columns}
            rows={pageRows}
            page={{ pageIndex: page, pageSize: PAGE_SIZE, rowCount: rows.length }}
            onPageChange={setPageIndex}
            pending={query.isLoading}
            emptyMessage={empty}
          />
        )}
        <p className="mt-2 text-sm text-muted-foreground">Each store and currency is ranked on its own; values are never converted.</p>
      </div>
    </div>
  );
}
